import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import {
  Plane,
  Globe2,
  Package,
  ShieldCheck,
  Clock3,
  MapPin,
  FileCheck,
  Boxes,
  ArrowRight,
  CheckCircle2,
  Truck,
  Warehouse,
  PlaneTakeoff,
  MoveRight
} from 'lucide-react'

import airFreightImage from '../assets/images/data/air-freight.png'
import CTA from './CTA'
import IndustryExpertise from './IndustryExpertise'
import MoreServicesSection from './MoreServicesSection'

const services = [
  {
    number: '01',
    icon: PlaneTakeoff,
    title: 'Airport-to-Airport',
    description:
      'Reliable air cargo movement between major domestic and international airports.'
  },
  {
    number: '02',
    icon: Package,
    title: 'Door-to-Door Delivery',
    description:
      'Complete pickup-to-delivery coordination for a smooth and convenient shipping experience.'
  },
  {
    number: '03',
    icon: Globe2,
    title: 'International Air Freight',
    description:
      'Global air cargo coordination to help connect your business with international markets.'
  },
  {
    number: '04',
    icon: FileCheck,
    title: 'Customs Documentation',
    description:
      'Documentation coordination and shipment support for efficient cargo movement.'
  },
  {
    number: '05',
    icon: Boxes,
    title: 'Special Cargo Handling',
    description:
      'Careful handling and coordination for fragile, valuable and time-sensitive consignments.'
  },
  {
    number: '06',
    icon: Truck,
    title: 'First & Last-Mile Connectivity',
    description:
      'Integrated road transportation connecting your warehouse, airport and final destination.'
  }
]

const benefits = [
  {
    icon: Clock3,
    title: 'Time-Critical Movement',
    description:
      'Choose air transportation for shipments where delivery speed matters.'
  },
  {
    icon: Globe2,
    title: 'Wider Connectivity',
    description:
      'Connect your shipments to domestic and international trade destinations.'
  },
  {
    icon: ShieldCheck,
    title: 'Careful Cargo Handling',
    description:
      'A coordinated approach to shipment safety, handling and documentation.'
  },
  {
    icon: MapPin,
    title: 'End-to-End Coordination',
    description:
      'Integrated pickup, airport handling and destination delivery support.'
  }
]

const process = [
  {
    number: '01',
    title: 'Shipment Enquiry',
    description:
      'Share your shipment details, cargo dimensions, weight and destination.'
  },
  {
    number: '02',
    title: 'Planning & Booking',
    description:
      'We coordinate the suitable air freight movement and shipment arrangements.'
  },
  {
    number: '03',
    title: 'Cargo Transportation',
    description:
      'Your shipment moves through the planned airport and transportation network.'
  },
  {
    number: '04',
    title: 'Final Delivery',
    description:
      'Destination coordination and delivery support complete the shipment journey.'
  }
]

const industries = [
  'Pharmaceuticals & Healthcare',
  'Electronics & Technology',
  'Automotive Components',
  'Fashion & Lifestyle',
  'Industrial Machinery',
  'E-commerce & Retail'
]

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
}

const sectionAnimation = {
  initial: 'hidden',
  whileInView: 'visible',
  viewport: { once: true, amount: 0.15 },
  variants: fadeUp,
  transition: { duration: 0.6 }
}

export default function AirFreightForwardingPage () {
  return (
    <>
      <Helmet>
        <title>Air Freight Forwarding Services | S Infinite Express</title>

        <meta
          name='description'
          content='S Infinite Express provides air freight forwarding, airport-to-airport transportation, international cargo coordination, customs documentation support and door-to-door logistics solutions.'
        />

        <meta
          name='keywords'
          content='Air Freight Forwarding, Air Cargo Services, International Air Freight, Domestic Air Cargo, Airport to Airport Cargo, Door to Door Air Freight, S Infinite Express'
        />

        <meta name='robots' content='index, follow' />

        <link
          rel='canonical'
          href='https://sinfiniteexpress.com/services/air-freight-forwarding'
        />

        <meta
          property='og:title'
          content='Air Freight Forwarding Services | S Infinite Express'
        />

        <meta
          property='og:description'
          content='Move your cargo faster with coordinated domestic and international air freight solutions.'
        />

        <meta
          property='og:url'
          content='https://sinfiniteexpress.com/services/air-freight-forwarding'
        />

        <meta property='og:type' content='website' />

        <meta
          property='og:image'
          content='https://sinfiniteexpress.com/static/media/air-freight.png'
        />

        <meta property='og:site_name' content='S Infinite Express' />
      </Helmet>

      <main className='overflow-hidden bg-white'>
        {/* HERO SECTION */}

        <section className='relative bg-gradient-to-br from-white via-blue-50/40 to-white overflow-hidden'>
          <div className='max-w-7xl mx-auto px-5 sm:px-8 lg:px-6 py-8 md:py-12'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center'>
              {/* LEFT CONTENT */}

              <motion.div {...sectionAnimation} className='space-y-6'>
                <h1 className='text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-extrabold text-[#211f57] leading-tight'>
                  Air Freight
                  <span className='text-[#d32c30]'> Forwarding</span>
                </h1>

                <div className='w-24 h-1.5 bg-[#d32c30] rounded-full' />

                <h2 className='text-xl sm:text-2xl font-bold text-[#211f57]'>
                  Connecting Businesses. Moving Cargo Faster.
                </h2>

                <p className='text-gray-600 text-base sm:text-lg leading-relaxed'>
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>{' '}
                  provides coordinated air freight forwarding solutions for
                  businesses that require efficient domestic and international
                  cargo movement. We connect shipments through dependable air
                  transportation planning and integrated logistics support.
                </p>

                <p className='text-gray-600 leading-relaxed'>
                  From airport-to-airport transportation and customs
                  documentation coordination to door-to-door delivery, our
                  services help simplify cargo movement and support your
                  business supply chain.
                </p>

                {/* ACTION BUTTONS */}

                <div className='flex flex-col sm:flex-row gap-4'>
                  <a
                    href='/contact-us'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#d32c30] text-white font-semibold rounded-xl shadow-lg hover:bg-[#b82629] hover:-translate-y-1 transition-all duration-300'
                  >
                    Get a Freight Quote
                    <MoveRight className='w-5 h-5' />
                  </a>

                  <a
                    href='#air-freight-services'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 border-2 border-[#211f57] text-[#211f57] font-semibold rounded-xl hover:bg-[#211f57] hover:text-white transition-all duration-300'
                  >
                    Explore Services
                    <ArrowRight className='w-5 h-5' />
                  </a>
                </div>

                {/* TRUST POINTS */}

                <div className='flex flex-wrap gap-x-4 gap-y-2 text-sm font-medium text-gray-600'>
                  {[
                    'Global Cargo',
                    'Careful Cargo Handling',
                    'End-to-End Coordination'
                  ].map(item => (
                    <span key={item} className='flex items-center gap-2'>
                      <CheckCircle2 className='w-4 h-4 text-green-600 shrink-0' />
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* RIGHT IMAGE */}

              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                <div className='absolute -top-6 -right-5 w-32 h-32 bg-[#fff44f]/60 rounded-full blur-3xl' />

                <div className='absolute -bottom-6 -left-5 w-32 h-32 bg-blue-200/70 rounded-full blur-3xl' />

                <div className='relative p-2 bg-white rounded-3xl shadow-2xl border border-gray-100'>
                  <img
                    src={airFreightImage}
                    alt='S Infinite Express air freight forwarding and cargo transportation'
                    className='w-full h-[200px] sm:h-[300px] lg:h-[420px] object-cover rounded-2xl'
                    loading='lazy'
                    decoding='async'
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* INTRODUCTION SECTION */}

        <section className='relative overflow-hidden bg-gradient-to-br from-yellow-50 via-blue-50/40 to-yellow-100/60 py-8 sm:py-10 lg:py-12'>
          <div className='absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-[90px]' />

          <div className='absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-200/30 blur-[90px]' />

          <div className='relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12'>
            <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16'>
              {/* LEFT CONTENT */}

              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className='space-y-6'
              >
                <h2 className='text-2xl font-bold leading-tight text-[#211f57] sm:text-2xl lg:text-3xl'>
                  Connecting Your Business Through
                  <span className='text-[#d32c30]'>
                    {' '}
                    Smarter Air Freight Solutions
                  </span>
                </h2>

                <p className='text-base leading-relaxed text-gray-600 sm:text-lg'>
                  At{' '}
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>
                  , we understand that efficient air transportation plays an
                  important role in modern supply chains. Our air freight
                  forwarding approach brings cargo transportation, shipment
                  coordination, documentation and delivery support together.
                </p>

                <p className='leading-relaxed text-gray-600'>
                  Whether you are moving urgent commercial shipments, valuable
                  consignments or international cargo, we focus on coordinated
                  logistics planning and reliable shipment handling.
                </p>

                {/* KEY FEATURES */}

                <div className='grid grid-cols-1 gap-x-5 gap-y-4 pt-3 sm:grid-cols-2'>
                  {[
                    'Domestic Air Freight',
                    'International Cargo',
                    'Customs Documentation',
                    'Door-to-Door Coordination'
                  ].map(item => (
                    <div key={item} className='flex items-center gap-3'>
                      <CheckCircle2
                        size={20}
                        className='shrink-0 text-green-600'
                      />

                      <span className='text-sm font-semibold text-[#211f57]'>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* RIGHT CAPABILITIES CARD */}

              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                <div className='absolute -right-5 -top-6 h-32 w-32 rounded-full bg-[#fff44f]/50 blur-3xl' />

                <div className='absolute -bottom-6 -left-5 h-32 w-32 rounded-full bg-blue-200/70 blur-3xl' />

                <div className='relative overflow-hidden rounded-3xl bg-[#211f57] p-3 shadow-2xl sm:p-4 lg:p-5'>
                  <div className='absolute -right-16 -top-16 h-48 w-48 rounded-full border-[25px] border-white/5' />

                  <div className='absolute -bottom-20 -right-10 h-56 w-56 rounded-full border-[25px] border-white/5' />

                  <div className='relative z-10'>
                    <span className='text-xs font-bold uppercase tracking-[2px] text-[#fff44f]'>
                      Our Capabilities
                    </span>

                    <p className='mb-2 mt-3 text-sm leading-7 text-white/70 sm:text-base'>
                      Integrated air freight services designed to simplify cargo
                      movement, improve shipment coordination and support your
                      business transportation requirements.
                    </p>

                    <div className='space-y-2'>
                      {[
                        {
                          title: 'Airport-to-Airport',
                          description:
                            'Coordinated air cargo movement between destinations.'
                        },
                        {
                          title: 'International Air Freight',
                          description:
                            'Cargo connectivity for international trade.'
                        },
                        {
                          title: 'Customs Documentation',
                          description:
                            'Documentation and shipment coordination support.'
                        },
                        {
                          title: 'Door-to-Door Delivery',
                          description:
                            'Integrated pickup and final destination delivery.'
                        }
                      ].map(item => (
                        <div
                          key={item.title}
                          className='flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition-all duration-300 hover:bg-white/10'
                        >
                          <div>
                            <h4 className='text-sm font-bold text-white sm:text-base'>
                              {item.title}
                            </h4>

                            <p className='mt-1 text-xs leading-5 text-white/55 sm:text-sm'>
                              {item.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* AIR FREIGHT SERVICES */}

        <section
          id='air-freight-services'
          className='py-6 bg-[#f7f8fc] scroll-mt-24'
        >
          <div className='max-w-7xl mx-auto px-6'>
            <div className='max-w-4xl mx-auto text-center mb-4'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                Our Capabilities
              </span>

              <h2 className='text-3xl sm:text-4xl font-extrabold text-[#211f57]'>
                Complete Air Freight Solutions
              </h2>

              <p className='text-gray-500 leading-7'>
                Flexible air cargo services designed to support different
                shipment requirements and business operations.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-6'>
              {services.map((service, index) => {
                const Icon = service.icon

                return (
                  <motion.div
                    key={service.number}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.08
                    }}
                    className='group bg-white p-7 rounded-2xl border border-gray-100 hover:border-[#d32c30]/30 hover:shadow-xl transition-all duration-300'
                  >
                    <div className='flex justify-between items-center'>
                      <div className='w-14 h-14 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] flex items-center justify-center text-white transition-colors'>
                        <Icon size={26} />
                      </div>

                      <span className='text-3xl font-black text-gray-100 group-hover:text-[#d32c30]/20 transition-colors'>
                        {service.number}
                      </span>
                    </div>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-3'>
                      {service.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-2'>
                      {service.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* INDUSTRY EXPERTISE */}

        <IndustryExpertise />

        {/* WHY CHOOSE US */}

        <section className='relative overflow-hidden bg-gradient-to-br from-yellow-50 via-blue-50/40 to-yellow-100/60 py-4 sm:py-6 lg:py-8'>
          <div className='absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-[90px]' />

          <div className='absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-200/30 blur-[90px]' />

          <div className='relative max-w-7xl mx-auto px-6'>
            <div className='text-center max-w-4xl mx-auto mb-5'>
              <span className='text-[#d32c30] font-extrabold uppercase tracking-[2px] text-xs'>
                The S Infinite Advantage
              </span>

              <h2 className='text-3xl sm:text-4xl font-extrabold text-[#211f57]'>
                Why Choose Our Air Freight Services?
              </h2>

              <p className='text-gray-500 leading-7'>
                Dependable cargo coordination, wider connectivity and integrated
                logistics support for your business shipments.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-6'>
              {benefits.map((item, index) => {
                const Icon = item.icon

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.08
                    }}
                    className='group bg-white p-7 rounded-2xl border border-gray-100 hover:border-[#d32c30]/30 hover:shadow-xl transition-all duration-300'
                  >
                    <div className='w-14 h-14 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] flex items-center justify-center text-white transition-colors'>
                      <Icon size={26} />
                    </div>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-4'>
                      {item.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-2'>
                      {item.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* OPERATIONAL CAPABILITIES */}

        <section className='py-8 sm:py-10 lg:py-12 bg-white'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 items-center'>
              <motion.div {...sectionAnimation}>
                <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                  Integrated Capabilities
                </span>

                <h2 className='text-2xl sm:text-3xl font-extrabold text-[#211f57] mt-2 leading-tight'>
                  Complete{' '}
                  <span className='text-[#d32c30]'>Air Freight Support.</span>
                </h2>

                <p className='text-gray-600 leading-8 mt-5'>
                  Bring essential air cargo activities together through an
                  integrated logistics approach. Our services support shipment
                  planning, cargo transportation, documentation, airport
                  coordination and destination delivery.
                </p>

                <p className='text-gray-600 leading-8 mt-3'>
                  Whether you need domestic air freight, international cargo
                  coordination or first and last-mile connectivity, our approach
                  focuses on simplifying your shipment journey.
                </p>

                <a
                  href='/contact-us'
                  className='inline-flex items-center gap-3 mt-7 px-6 py-4 bg-[#d32c30] text-white text-sm font-bold rounded-xl shadow-lg hover:bg-[#b82629] hover:-translate-y-1 transition-all duration-300'
                >
                  Discuss Your Air Freight Needs
                  <ArrowRight size={17} />
                </a>
              </motion.div>

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                {[
                  {
                    icon: Plane,
                    title: 'Air Cargo Movement',
                    description:
                      'Coordinated domestic and international cargo transportation.'
                  },
                  {
                    icon: FileCheck,
                    title: 'Documentation Support',
                    description:
                      'Shipment documentation and customs coordination.'
                  },
                  {
                    icon: Warehouse,
                    title: 'Cargo Handling',
                    description:
                      'Organized cargo handling and shipment processing.'
                  },
                  {
                    icon: Truck,
                    title: 'Last-Mile Connectivity',
                    description:
                      'Road transportation from airport to final destination.'
                  },
                  {
                    icon: Globe2,
                    title: 'Global Connectivity',
                    description:
                      'Air freight coordination for international destinations.'
                  },
                  {
                    icon: Boxes,
                    title: 'Special Cargo',
                    description:
                      'Careful coordination for valuable and sensitive cargo.'
                  }
                ].map((item, index) => {
                  const Icon = item.icon

                  return (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.4,
                        delay: index * 0.07
                      }}
                      className='group bg-[#f7f8fc] border border-gray-100 rounded-2xl p-6 hover:border-[#d32c30]/30 hover:shadow-lg transition-all duration-300'
                    >
                      <div className='w-12 h-12 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] text-white flex items-center justify-center transition-colors'>
                        <Icon size={23} />
                      </div>

                      <h3 className='text-base font-extrabold text-[#211f57] mt-4'>
                        {item.title}
                      </h3>

                      <p className='text-sm text-gray-500 leading-6 mt-2'>
                        {item.description}
                      </p>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* PROCESS SECTION */}

        <section className='py-4 lg:py-5 bg-[#f7f8fc]'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='text-center max-w-2xl mx-auto mb-4'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                How It Works
              </span>

              <h2 className='text-2xl sm:text-3xl font-extrabold text-[#211f57]'>
                From Pickup to Destination
              </h2>

              <p className='text-gray-500 leading-7'>
                A coordinated process that makes air cargo movement easier to
                manage.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-8 relative'>
              {process.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1
                  }}
                  className='relative text-center'
                >
                  <div className='w-16 h-16 mx-auto rounded-2xl bg-[#211f57] text-white flex items-center justify-center text-xl font-extrabold shadow-lg'>
                    {step.number}
                  </div>

                  <h3 className='text-lg font-extrabold text-[#211f57]'>
                    {step.title}
                  </h3>

                  <p className='text-sm text-gray-500 leading-7'>
                    {step.description}
                  </p>

                  {index !== process.length - 1 && (
                    <div className='hidden lg:block absolute top-8 left-[calc(50%+45px)] w-[calc(100%-50px)] border-t-2 border-dashed border-[#d32c30]/30' />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* INDUSTRIES WE SERVE */}
        <section className='py-4 sm:py-5 lg:py-6 bg-white'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 items-center'>
              <div>
                <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                  Industries We Serve
                </span>

                <h2 className='text-2xl sm:text-3xl font-extrabold text-[#211f57] leading-tight'>
                  Supporting Diverse
                  <span className='text-[#d32c30]'>
                    {' '}
                    Business Requirements.
                  </span>
                </h2>

                <p className='text-gray-600 leading-8'>
                  Different industries have different cargo requirements. Our
                  air freight coordination helps businesses move a wide range of
                  commercial shipments through flexible transportation and
                  logistics support.
                </p>

                <p className='text-gray-600 leading-8'>
                  From healthcare and electronics to retail and industrial
                  shipments, we help coordinate cargo movement around business
                  requirements.
                </p>
              </div>

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                {industries.map((industry, index) => (
                  <motion.div
                    key={industry}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.07
                    }}
                    className='group flex items-center gap-4 bg-[#f7f8fc] border border-gray-100 rounded-2xl p-5 hover:border-[#d32c30]/30 hover:shadow-lg transition-all duration-300'
                  >
                    <span className='text-sm font-bold text-[#211f57]'>
                      {industry}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}

        <section className='relative bg-[#211f57] py-4 overflow-hidden'>
          <div className='absolute right-0 top-0 w-96 h-96 rounded-full border-[50px] border-white/5 translate-x-1/3 -translate-y-1/3' />
          <div className='relative max-w-5xl mx-auto px-6 text-center'>
            <h2 className='text-2xl sm:text-3xl lg:text-3xl font-extrabold text-white leading-tight'>
              Looking for Reliable Air Freight Solutions?
              <span className='block text-[#FFF44F]'>
                Let's Take Your Business Further.
              </span>
            </h2>

            <p className='text-white/70 max-w-4xl mx-auto leading-8'>
              Connect with S Infinite Express for reliable air freight, cargo
              handling and door-to-door delivery solutions.
            </p>
          </div>
        </section>

        {/* MORE SERVICES */}
        <MoreServicesSection />

        {/* COMMON CTA */}
        <CTA />
      </main>
    </>
  )
}
